/* eslint-disable react/no-danger */
import Prismic from '@prismicio/client';
import {
  GetStaticPaths,
  GetStaticProps,
  GetStaticPropsContext,
  NextPage,
} from 'next';
import Head from 'next/head';
import SliceZone from 'next-slicezone';
import { Document } from '@prismicio/client/types/documents';
import { ParsedUrlQuery } from 'querystring';
import { Ref } from '@prismicio/client/types/ResolvedApi';
import set from 'lodash.set';
import cloneDeep from 'lodash.clonedeep';
import { nanoid } from 'nanoid';
import { CountryCodes } from '@digitalhouse-tech/ts-lib-checkout-url-mapper/.dist/util/countryMap';
import * as Sentry from '@sentry/nextjs';
import { AccordionVariations } from 'slices/Accordion/Accordion.types';
import { FormVariations } from 'slices/Form/Form.types';
import { GridListsVariations } from 'slices/GridLists/GridLists.types';
import { EPricingVariations } from 'slices/Pricing/Pricing.types';
import { TabsVariations } from 'slices/Tabs/Tabs.types';
import { IProduct } from '../../../../graphql/model/interfaces';
import {
  applyProductFormat, getDefaultOffer, getOffers, getProducts,
  getRelevantProduct,
} from '../../../../utils/checkout';
import { getStructuredDataCluster } from '../../../../utils/structuredData';
import {
  getCountry,
  getCurrencyCode,
  getCurrencySymbol,
  getMetaAlternates,
  getMetaText,
  getRealLocale,
  getUidPath,
  getVerticalId,
} from '../../../../utils/common';
import useUpdatePreviewRef from '../../../../hooks/useUpdatePreviewRef';
import Layout from '../../../../components/Layout';
import resolver from '../../../../sm-resolver';
import { PRISMIC_API_ENDPOINT, SPANISH_LOCALE, PUBLIC_DEFAULT_LOCALE } from '../../../../utils/constants';

interface Props {
  header: Document;
  page: Document;
  footer: Document;
  clustersAlternate: Document[];
  preview: { activeRef: Ref };
  checkoutData: any;
}

type Page = NextPage<Props> & { Layout?: React.FC };

const Page: Page = ({
  page, header, footer, preview, clustersAlternate: clusters, checkoutData,
}) => {
  useUpdatePreviewRef(preview, page.id);
  const metaTitle = getMetaText(page.data?.meta_title, page.lang);
  const metaDescription = getMetaText(page.data?.meta_description, page.lang);
  const metaKeywords = page.data?.meta_keywords?.map(({ word }) => word)?.filter(Boolean) ?? [];
  const metaAlternate = getMetaAlternates({ pages: [cloneDeep(page), ...page.alternate_languages], clusters });

  return (
    <Layout header={header} footer={footer} resolver={resolver} product={getRelevantProduct(checkoutData)}>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={
            {
              __html: getStructuredDataCluster(page.lang as string, page.data?.meta_title, page.data?.meta_description),
            }
          }
        />
        <title>{metaTitle}</title>
        <meta property="og:title" content={metaTitle} key="title" />
        <meta name="description" content={metaDescription} />
        {!!metaKeywords.length && <meta name="keywords" content={metaKeywords.join(', ')} />}
        {metaKeywords.map((word) => <meta key={nanoid()} property="article:tag" content={word} />)}
        {metaAlternate.map(({ lang, href }) => (<link key={nanoid()} rel="alternate" hrefLang={lang} href={href} />))}
      </Head>
      <SliceZone resolver={resolver} slices={page.data?.slices} />
    </Layout>
  );
};

type TPreviewData = GetStaticPropsContext & { previewData?};

export const getStaticProps: GetStaticProps = async (context: TPreviewData) => {
  const {
    locale,
    params,
    previewData,
  } = context;
  const ref = previewData?.ref ?? null;
  let checkoutData: any;
  const currentLocale = getRealLocale(
    locale || PUBLIC_DEFAULT_LOCALE || SPANISH_LOCALE,
  );

  const country = getCountry(locale || PUBLIC_DEFAULT_LOCALE || SPANISH_LOCALE);

  const currencyCode = getCurrencyCode(locale || PUBLIC_DEFAULT_LOCALE || SPANISH_LOCALE);

  const currencySymbol = getCurrencySymbol(locale || PUBLIC_DEFAULT_LOCALE || SPANISH_LOCALE);

  const client = Prismic.client(PRISMIC_API_ENDPOINT);

  const headerRequest = client.getSingle('header', {
    lang: currentLocale,
    ref,
  });

  const footerRequest = client.getSingle('footer', {
    lang: currentLocale,
    ref,
  });

  const verticalsRequest = client.query(
    [Prismic.predicates.at('document.type', 'verticalpage')],
    {
      lang: currentLocale,
      ref,
    },
  );

  const clustersRequest = client.query(
    [Prismic.predicates.at('document.type', 'clusterpage')],
    {
      lang: currentLocale,
      ref,
      pageSize: 100,
    },
  );

  const [header, footer, verticals, clusters] = await Promise.all(
    [headerRequest, footerRequest, verticalsRequest, clustersRequest],
  );

  const page = clusters.results.find((item) => item.uid === params?.clusterid);
  const vertical = verticals.results.find((item) => item.uid === params?.verticalid);
  const clustersAlternate = page?.alternate_languages
    ? (await client.getByIDs(page.alternate_languages.map(({ id }) => id), { lang: '*', ref })).results
    : [];

  if (page?.data?.slices) {
    const cluster = cloneDeep(page);
    const tokens = page?.data?.offerId?.split(',') ?? [];

    let productsNextStarts: IProduct[] = [];
    let allProducts: IProduct[] = [];
    const wareId = page?.data?.wareId?.split(',') || [];

    try {
      let offersCopy: any[] = [];
      const checkoutProducts: any[] = [];

      const promisesP = wareId.map((ware) => getProducts(ware, null, country));
      const resultsP = await Promise.all(promisesP);

      resultsP.forEach((tmpPod: any) => {
        if (tmpPod?.data?.products1?.[0]) checkoutProducts.push(tmpPod.data.products1[0]);
      });

      if (checkoutProducts?.length) {
        const defaultOffers: any[] = [];

        const promisesDO = wareId.map((ware) => getDefaultOffer(ware));
        const resultsDO = await Promise.all(promisesDO);

        resultsDO.forEach((tmpPod: any) => {
          if (tmpPod?.offer1) defaultOffers.push(tmpPod.offer1);
        });

        if (tokens.length) {
          const offers: any[] = [];
          const promisesO = wareId.map((ware, index) => getOffers([tokens[index]], [ware]).then((tmpPod) => {
            if (tmpPod) {
              return { wareId: ware, offerId: tokens[index], ...tmpPod };
            }
            return null;
          }));

          const resultsO = await Promise.all(promisesO);

          resultsO.forEach((result) => {
            if (result) {
              offers.push(result);
            }
          });

          /** @todo: no se porque pero si le saco el cloneDeep deja de andar :) */
          offersCopy = cloneDeep(defaultOffers);

          offers.forEach((offer) => {
            const offerWareIndex = defaultOffers.findIndex((d) => d.id === offer.wareId);
            let plansMerged: any[] = [];
            if (offer?.offer1?.plans) plansMerged = [...plansMerged, ...offer.offer1.plans];

            if (offersCopy[offerWareIndex]?.defaultOffer) {
              offersCopy[offerWareIndex].defaultOffer.plans = plansMerged.map(
                (plan) => ({ ...plan, offerId: offer.offerId }),
              );
            }
          });
        }
        const formattedProducts = applyProductFormat(
          wareId,
          country,
          currencyCode,
          currencySymbol,
          checkoutProducts,
          offersCopy,
        );

        productsNextStarts = formattedProducts.productsNextStarts;
        allProducts = formattedProducts.products;
      }
    } catch (err) {
      Sentry.captureException(err, {
        offerId: page?.data?.offerId ?? null,
        wareId: page?.data?.wareId ?? null,
        currentLocale,
      } as any);
    }
    checkoutData = wareId.length ? {
      products: productsNextStarts || null,
      allProducts: allProducts || null,
      country: CountryCodes[country] || null,
      sandbox: process.env.NEXT_PUBLIC_CHECKOUT_SANDBOX === 'true',
    } : null;
    const slimVerticals = verticals?.results?.map((item) => (
      {
        uid: item.uid, type: item.type, lang: item.lang, data: { color: item.data?.color ?? null },
      }
    ));
    const slimClusters = clusters?.results?.map((item) => (
      {
        uid: item.uid,
        type: item.type,
        lang: item.lang,
        data: {
          verticalPage: { uid: item.data?.verticalPage?.uid ?? null },
        },
      }
    ));

    const slicesWithCheckout = [
      AccordionVariations.withTabs,
      FormVariations.contactSchedule,
      GridListsVariations.productsCheckout,
      EPricingVariations.pricingCards,
      TabsVariations.productCardCheckout,
    ];

    page.data.slices = page.data.slices.map((slice) => ({
      ...slice,
      _checkout: slicesWithCheckout.includes(slice.variation) ? checkoutData : null,
      _vertical: vertical || null,
      _verticals: slimVerticals ?? null,
      _cluster: cluster,
      _clusters: slimClusters ?? null,
      _page: {
        type: page.type,
      },
    }));
  }

  if (!header || !footer || !page) {
    const extra = {
      ...params,
    } as any;
    if (!header) {
      Sentry.captureException(new Error('Header not found'), extra);
    } else if (!footer) {
      Sentry.captureException(new Error('Footer not found'), extra);
    }

    return {
      notFound: true,
      revalidate: 300,
    };
  }

  const products = header.data.products.map(
    (item) => set(item, 'link.data.verticalId', getVerticalId(item.link.uid, clusters.results)),
  );

  return {
    props: {
      checkoutData,
      clustersAlternate,
      header: {
        ...header.data,
        products,
        clusters: clusters.results,
        verticals: verticals.results,
      },
      footer: footer.data,
      page,
      preview: {
        activeRef: ref,
      },
    },
    revalidate: 300,
  };
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const client = Prismic.client(PRISMIC_API_ENDPOINT);

  const pages = await client.query(
    [Prismic.Predicates.at('document.type', 'clusterpage')],
    {
      lang: '*',
      pageSize: 100,
    },
  );

  const paths: {
    params: ParsedUrlQuery;
    locale?: string | undefined;
  }[] = [];

  locales?.forEach((locale) => {
    pages.results.forEach(({
      uid, lang, data,
    }) => {
      const verticalid = data?.verticalPage?.uid;

      if (
        uid
        && lang
        && getRealLocale(locale).toLowerCase() === lang.toLowerCase()
        && verticalid
      ) {
        paths.push({
          params: {
            uid: getUidPath('products', locale),
            verticalid,
            clusterid: uid,
          },
          locale,
        });
      }
    });
  });

  return {
    paths,
    fallback: 'blocking',
  };
};

export default Page;
